import React, { useState, useEffect } from 'react';
import { generateXrayRightShoulderReport } from '../../utils/xrayrightshoulder';
import { API_BASE_URL } from '../Api/apiconnector';

export default function XRayShoulderForm({ onClose, currentUser, generateReport, onSubmit }) {
  const [formData, setFormData] = useState({
    // Patient Info (matching report function field names)
    NameTextFR12: '',
    IDTextFR12: '',
    AgeTextFR12: '',
    TestDateTextFR12: '',
    ReportDateTextFR12: '',
    GenderTextFR12: '',
    ReferralDrTextFR12: '',
    ReporttimeTextFR12: '',
    xrayType: '',
    
    // Main conditions
    Normal: false,
    fracture: false,
    dislocation: false,
    degenerative: false,
    
    // Fracture options
    humerus: false,
    clavicle: false,
    scapula: false,
    ribsFracture: false,
    ribText: '',
    
    // Humerus fracture types
    linearFracture: false,
    comminutedFracture: false,
    obliqueFracture: false,
    
    // Linear fracture locations
    LinearHead: false,
    LinearNeck: false,
    LinearProximal: false,
    
    // Comminuted fracture locations
    comminutedHead: false,
    comminutedNeck: false,
    comminutedProximal: false,
    
    // Oblique fracture locations
    obliqueHead: false,
    obliqueNeck: false,
    obliqueProximal: false,
    
    // Clavicle locations
    clavicleProximalShaft: false,
    clavicleMidShaft: false,
    clavicleDistalShaft: false,
    
    // Scapula locations
    scapulaBody: false,
    scapulaSpine: false,
    scapulaGlenoid: false,
    
    // Dislocation details
    acromioclavicularJoint: false,
    acromioclavicularJointType: '',
    shoulderJoint: false,
    shoulderJointType: '',
    
    // Degenerative details
    glenohumeralJoint: false,
    acromioclavicularJointDegenerative: false, // Renamed to avoid conflict
    glenohumeralJointType: '',
    glenohumeralJointJointSpace: false,
    glenohumeralJointSubchondralCyst: false,
    glenohumeralJointMarginalOsteophytes: false,
    glenohumeralJointIrregularArticular: false,
    glenohumeralJointSclerosis: false
  });

  useEffect(() => {
    const fetchDemographics = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const patientId = urlParams.get("id");
        if (!patientId) return;

        const response = await fetch(`${API_BASE_URL}/fetch-dicom/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ id: patientId }),
        });

        if (!response.ok)
          throw new Error(`Failed to fetch demographics (${response.status})`);
        const data = await response.json();
        console.log("✅ Fetched demographics:", data);

        const formattedStudyDate = data.study_date || "";
        const now = new Date();
        const formattedReportDate = formatToDDMMYYYY(now);
        const formattedReportTime = now.toLocaleTimeString("en-GB");

        setFormData((prev) => ({
          ...prev,
          NameTextFR12: data.patient_name || "",
          IDTextFR12: data.patient_id || "",
          AgeTextFR12: data.age || "",
          GenderTextFR12: data.gender || "Male",
          ReferralDrTextFR12: data.referring_doctor_name || "",
          TestDateTextFR12: formattedStudyDate,
          ReportDateTextFR12: formattedReportDate,
          ReporttimeTextFR12: formattedReportTime,
        }));
      } catch (error) {
        console.error("❌ Error fetching DICOM demographics:", error);
      }
    };

    fetchDemographics();
  }, []);

  // Helper function
  function formatToDDMMYYYY(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Function to map form data to the expected format for generateXrayRightShoulderReport
  const mapFormDataToReportFormat = () => {
    return {
      // Patient Information
      NameTextFR12: formData.NameTextFR12,
      IDTextFR12: formData.IDTextFR12,
      AgeTextFR12: formData.AgeTextFR12,
      GenderTextFR12: formData.GenderTextFR12,
      ReferralDrTextFR12: formData.ReferralDrTextFR12,
      TestDateTextFR12: formData.TestDateTextFR12,
      ReportDateTextFR12: formData.ReportDateTextFR12,
      ReporttimeTextFR12: formData.ReporttimeTextFR12,
      
      // X-Ray Type and Normal
      xrayType: formData.xrayType,
      Normal: formData.Normal,
      
      // Fracture Data
      fracture: formData.fracture,
      humerus: formData.humerus,
      clavicle: formData.clavicle,
      scapula: formData.scapula,
      ribsFracture: formData.ribsFracture,
      ribText: formData.ribText,
      
      // Humerus Fracture Types
      linearFracture: formData.linearFracture,
      comminutedFracture: formData.comminutedFracture,
      obliqueFracture: formData.obliqueFracture,
      
      // Linear Fracture Locations
      LinearHead: formData.LinearHead,
      LinearNeck: formData.LinearNeck,
      LinearProximal: formData.LinearProximal,
      
      // Comminuted Fracture Locations
      comminutedHead: formData.comminutedHead,
      comminutedNeck: formData.comminutedNeck,
      comminutedProximal: formData.comminutedProximal,
      
      // Oblique Fracture Locations
      obliqueHead: formData.obliqueHead,
      obliqueNeck: formData.obliqueNeck,
      obliqueProximal: formData.obliqueProximal,
      
      // Clavicle Locations
      clavicleProximalShaft: formData.clavicleProximalShaft,
      clavicleMidShaft: formData.clavicleMidShaft,
      clavicleDistalShaft: formData.clavicleDistalShaft,
      
      // Scapula Locations
      scapulaBody: formData.scapulaBody,
      scapulaSpine: formData.scapulaSpine,
      scapulaGlenoid: formData.scapulaGlenoid,
      
      // Dislocation Details
      dislocation: formData.dislocation,
      acromioclavicularJoint: formData.acromioclavicularJoint,
      acromioclavicularJointType: formData.acromioclavicularJointType,
      shoulderJoint: formData.shoulderJoint,
      shoulderJointType: formData.shoulderJointType,
      
      // Degenerative Details
      degenerative: formData.degenerative,
      glenohumeralJoint: formData.glenohumeralJoint,
      acromioclavicularJointDegenerative: formData.acromioclavicularJointDegenerative,
      glenohumeralJointType: formData.glenohumeralJointType,
      glenohumeralJointJointSpace: formData.glenohumeralJointJointSpace,
      glenohumeralJointSubchondralCyst: formData.glenohumeralJointSubchondralCyst,
      glenohumeralJointMarginalOsteophytes: formData.glenohumeralJointMarginalOsteophytes,
      glenohumeralJointIrregularArticular: formData.glenohumeralJointIrregularArticular,
      glenohumeralJointSclerosis: formData.glenohumeralJointSclerosis
    };
  };

  const handleRadioChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBack = () => {
    console.log('Back clicked');
    if (onClose) {
      onClose();
    }
  };

  // Modified handleDone to integrate with CKEditor
  const handleDone = () => {
    console.log('Form submitted:', formData);
    
    // Map form data to the expected format
    const reportData = mapFormDataToReportFormat();
    
    // Generate the report
    const report = generateXrayRightShoulderReport(reportData, currentUser);
    
    console.log('Generated Report:', report);
    
    // Pass the report to the parent component (to be displayed in editor)
    if (generateReport) {
      generateReport(report);
    }
    
    console.log("✅ Report generated and sent to editor", report);
    
    // Show success message
    alert('Form submitted successfully! Report generated in editor.');
    
    // Close the form
    if (onClose) {
      onClose();
    }
  };

  // Alternative: If you want to keep the form open to review the generated report
  const handleSubmitAndPreview = () => {
    const reportData = mapFormDataToReportFormat();
    const generatedReport = generateXrayRightShoulderReport(reportData, currentUser);
    
    if (onSubmit) {
      onSubmit(generatedReport);
    }
    
    console.log("✅ Report generated and preview sent to editor", generatedReport);
    
    // You can show a success message instead of immediately closing
    alert("Report generated successfully! Check the editor for the generated content.");
  };

  const handleReject = () => {
    console.log('Form rejected');
    alert('Form rejected!');
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg flex flex-col" style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b">
          <h1 className="text-xl font-bold">X-RAY RIGHT-SHOULDER</h1>
          <div className="flex gap-2">
            <button 
              onClick={handleBack}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back
            </button>
            <button 
              onClick={handleDone}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Done
            </button>
            <button 
              onClick={handleReject}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Reject
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Patient Info - Editable Fields */}
          <div className="grid grid-cols-5 gap-4 text-sm mb-6">
            <div>
              <label className="block text-gray-600 mb-1">Name</label>
              <input
                type="text"
                value={formData.NameTextFR12}
                onChange={(e) => setFormData(prev => ({ ...prev, NameTextFR12: e.target.value }))}
                className="w-full px-3 py-2 border rounded"
                placeholder="Enter name"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Patient ID</label>
              <input
                type="text"
                value={formData.IDTextFR12}
                onChange={(e) => setFormData(prev => ({ ...prev, IDTextFR12: e.target.value }))}
                className="w-full px-3 py-2 border rounded"
                placeholder="Enter ID"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Age</label>
              <input
                type="text"
                value={formData.AgeTextFR12}
                onChange={(e) => setFormData(prev => ({ ...prev, AgeTextFR12: e.target.value }))}
                className="w-full px-3 py-2 border rounded"
                placeholder="Enter age"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Test date</label>
              <input
                type="text"
                value={formData.TestDateTextFR12}
                onChange={(e) => setFormData(prev => ({ ...prev, TestDateTextFR12: e.target.value }))}
                className="w-full px-3 py-2 border rounded"
                placeholder="DD-MM-YYYY"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Report date</label>
              <input
                type="text"
                value={formData.ReportDateTextFR12}
                onChange={(e) => setFormData(prev => ({ ...prev, ReportDateTextFR12: e.target.value }))}
                className="w-full px-3 py-2 border rounded"
                placeholder="DD-MM-YYYY"
              />
            </div>
          </div>

          {/* Gender */}
          <div className="mb-6">
            <div className="text-center text-gray-600 mb-2">Gender</div>
            <div className="flex justify-center gap-6">
              {['Male', 'Female', 'Others'].map(gender => (
                <label key={gender} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.GenderTextFR12 === gender}
                    onChange={() => handleRadioChange('GenderTextFR12', gender)}
                    className="w-4 h-4"
                  />
                  <span>{gender}</span>
                </label>
              ))}
            </div>
          </div>

          {/* X-Ray Type */}
          <div className="mb-6">
            <div className="text-center text-gray-600 mb-2">X-Ray Type</div>
            <div className="flex justify-center gap-6">
              {['AP', 'AXIAL', 'LATERAL'].map(type => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.xrayType === type}
                    onChange={() => handleRadioChange('xrayType', type)}
                    className="w-4 h-4"
                  />
                  <span>{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Main Checkboxes */}
          <div className="space-y-4 mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.Normal}
                onChange={() => handleCheckboxChange('Normal')}
                className="w-5 h-5 accent-pink-600"
              />
              <span>Normal Right-Shoulder?</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.fracture}
                onChange={() => handleCheckboxChange('fracture')}
                className="w-5 h-5 accent-pink-600"
              />
              <span>Fracture?</span>
            </label>

            {/* Fracture Details */}
            {formData.fracture && (
              <div className="ml-8 space-y-4 border-l-2 border-gray-200 pl-6">
                {/* Humerus, Clavicle, Scapula, Ribs Fracture */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.humerus}
                      onChange={() => handleCheckboxChange('humerus')}
                      className="w-5 h-5 accent-pink-600"
                    />
                    <span>Humerus?</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.clavicle}
                      onChange={() => handleCheckboxChange('clavicle')}
                      className="w-5 h-5 accent-pink-600"
                    />
                    <span>Clavicle?</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.scapula}
                      onChange={() => handleCheckboxChange('scapula')}
                      className="w-5 h-5 accent-pink-600"
                    />
                    <span>Scapula</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.ribsFracture}
                      onChange={() => handleCheckboxChange('ribsFracture')}
                      className="w-5 h-5 accent-pink-600"
                    />
                    <span>Ribs Fracture</span>
                  </label>
                  
                  {formData.ribsFracture && (
                    <div className="ml-4">
                      <input
                        type="text"
                        value={formData.ribText}
                        onChange={(e) => handleInputChange('ribText', e.target.value)}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="Enter ribs fracture details"
                      />
                    </div>
                  )}
                </div>

                {/* Humerus Details */}
                {formData.humerus && (
                  <div className="ml-4 space-y-4 border-l-2 border-gray-200 pl-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.linearFracture}
                        onChange={() => handleCheckboxChange('linearFracture')}
                        className="w-5 h-5 accent-pink-600"
                      />
                      <span>Linear</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.comminutedFracture}
                        onChange={() => handleCheckboxChange('comminutedFracture')}
                        className="w-5 h-5 accent-pink-600"
                      />
                      <span>Comminuted Fracture</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.obliqueFracture}
                        onChange={() => handleCheckboxChange('obliqueFracture')}
                        className="w-5 h-5 accent-pink-600"
                      />
                      <span>Oblique fracture</span>
                    </label>

                    {/* Linear Fracture Locations */}
                    {formData.linearFracture && (
                      <div className="ml-4 space-y-2 border-l-2 border-gray-200 pl-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.LinearHead}
                            onChange={() => handleCheckboxChange('LinearHead')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Head?</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.LinearNeck}
                            onChange={() => handleCheckboxChange('LinearNeck')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Neck?</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.LinearProximal}
                            onChange={() => handleCheckboxChange('LinearProximal')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Proximal Diaphysis?</span>
                        </label>
                      </div>
                    )}

                    {/* Comminuted Fracture Locations */}
                    {formData.comminutedFracture && (
                      <div className="ml-4 space-y-2 border-l-2 border-gray-200 pl-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.comminutedHead}
                            onChange={() => handleCheckboxChange('comminutedHead')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Head?</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.comminutedNeck}
                            onChange={() => handleCheckboxChange('comminutedNeck')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Neck?</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.comminutedProximal}
                            onChange={() => handleCheckboxChange('comminutedProximal')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Proximal Diaphysis?</span>
                        </label>
                      </div>
                    )}

                    {/* Oblique Fracture Locations */}
                    {formData.obliqueFracture && (
                      <div className="ml-4 space-y-2 border-l-2 border-gray-200 pl-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.obliqueHead}
                            onChange={() => handleCheckboxChange('obliqueHead')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Head?</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.obliqueNeck}
                            onChange={() => handleCheckboxChange('obliqueNeck')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Neck?</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.obliqueProximal}
                            onChange={() => handleCheckboxChange('obliqueProximal')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Proximal Diaphysis?</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}

                {/* Clavicle Details */}
                {formData.clavicle && (
                  <div className="ml-4 space-y-2 border-l-2 border-gray-200 pl-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.clavicleProximalShaft}
                        onChange={() => handleCheckboxChange('clavicleProximalShaft')}
                        className="w-5 h-5 accent-pink-600"
                      />
                      <span>Proximal One Third</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.clavicleMidShaft}
                        onChange={() => handleCheckboxChange('clavicleMidShaft')}
                        className="w-5 h-5 accent-pink-600"
                      />
                      <span>Middle One Third</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.clavicleDistalShaft}
                        onChange={() => handleCheckboxChange('clavicleDistalShaft')}
                        className="w-5 h-5 accent-pink-600"
                      />
                      <span>Distal One Third</span>
                    </label>
                  </div>
                )}

                {/* Scapula Details */}
                {formData.scapula && (
                  <div className="ml-4 space-y-2 border-l-2 border-gray-200 pl-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.scapulaBody}
                        onChange={() => handleCheckboxChange('scapulaBody')}
                        className="w-5 h-5 accent-pink-600"
                      />
                      <span>Body</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.scapulaSpine}
                        onChange={() => handleCheckboxChange('scapulaSpine')}
                        className="w-5 h-5 accent-pink-600"
                      />
                      <span>Spine</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.scapulaGlenoid}
                        onChange={() => handleCheckboxChange('scapulaGlenoid')}
                        className="w-5 h-5 accent-pink-600"
                      />
                      <span>Glenoid</span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Dislocation Section */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.dislocation}
                onChange={() => handleCheckboxChange('dislocation')}
                className="w-5 h-5 accent-pink-600"
              />
              <span>Dislocation?</span>
            </label>

            {formData.dislocation && (
              <div className="ml-8 space-y-4 border-l-2 border-gray-200 pl-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.acromioclavicularJoint}
                    onChange={() => handleCheckboxChange('acromioclavicularJoint')}
                    className="w-5 h-5 accent-pink-600"
                  />
                  <span>Acromioclavicular joint?</span>
                </label>

                {formData.acromioclavicularJoint && (
                  <div className="ml-8 flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.acromioclavicularJointType === 'dislocated'}
                        onChange={() => handleRadioChange('acromioclavicularJointType', 'dislocated')}
                        className="w-4 h-4"
                      />
                      <span>dislocated</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.acromioclavicularJointType === 'subluxated'}
                        onChange={() => handleRadioChange('acromioclavicularJointType', 'subluxated')}
                        className="w-4 h-4"
                      />
                      <span>subluxated</span>
                    </label>
                  </div>
                )}

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.shoulderJoint}
                    onChange={() => handleCheckboxChange('shoulderJoint')}
                    className="w-5 h-5 accent-pink-600"
                  />
                  <span>Shoulder joint?</span>
                </label>

                {formData.shoulderJoint && (
                  <div className="ml-8 flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.shoulderJointType === 'anterior'}
                        onChange={() => handleRadioChange('shoulderJointType', 'anterior')}
                        className="w-4 h-4"
                      />
                      <span>anterior</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.shoulderJointType === 'posterior'}
                        onChange={() => handleRadioChange('shoulderJointType', 'posterior')}
                        className="w-4 h-4"
                      />
                      <span>posterior</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.shoulderJointType === 'inferior'}
                        onChange={() => handleRadioChange('shoulderJointType', 'inferior')}
                        className="w-4 h-4"
                      />
                      <span>inferior</span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Degenerative Section */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.degenerative}
                onChange={() => handleCheckboxChange('degenerative')}
                className="w-5 h-5 accent-pink-600"
              />
              <span>Degenerative?</span>
            </label>

            {formData.degenerative && (
              <div className="ml-8 space-y-4 border-l-2 border-gray-200 pl-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.glenohumeralJoint}
                    onChange={() => handleCheckboxChange('glenohumeralJoint')}
                    className="w-5 h-5 accent-pink-600"
                  />
                  <span>Glenohumeral Joint?</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.acromioclavicularJointDegenerative}
                    onChange={() => handleCheckboxChange('acromioclavicularJointDegenerative')}
                    className="w-5 h-5 accent-pink-600"
                  />
                  <span>Acromioclavicular Joint?</span>
                </label>

                {formData.glenohumeralJoint && (
                  <div className="ml-8 space-y-4 border-l-2 border-gray-200 pl-6">
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.glenohumeralJointType === 'Mild'}
                          onChange={() => handleRadioChange('glenohumeralJointType', 'Mild')}
                          className="w-4 h-4"
                        />
                        <span>Mild</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.glenohumeralJointType === 'Moderate'}
                          onChange={() => handleRadioChange('glenohumeralJointType', 'Moderate')}
                          className="w-4 h-4"
                        />
                        <span>Moderate</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.glenohumeralJointType === 'Severe'}
                          onChange={() => handleRadioChange('glenohumeralJointType', 'Severe')}
                          className="w-4 h-4"
                        />
                        <span>Severe</span>
                      </label>
                    </div>

                    {(formData.glenohumeralJointType === 'Mild' || formData.glenohumeralJointType === 'Moderate' || formData.glenohumeralJointType === 'Severe') && (
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.glenohumeralJointJointSpace}
                            onChange={() => handleCheckboxChange('glenohumeralJointJointSpace')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Joint Space</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.glenohumeralJointSubchondralCyst}
                            onChange={() => handleCheckboxChange('glenohumeralJointSubchondralCyst')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Subchondral Cyst</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.glenohumeralJointMarginalOsteophytes}
                            onChange={() => handleCheckboxChange('glenohumeralJointMarginalOsteophytes')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Marginal Osteophytes</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.glenohumeralJointIrregularArticular}
                            onChange={() => handleCheckboxChange('glenohumeralJointIrregularArticular')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Irregular Articular</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.glenohumeralJointSclerosis}
                            onChange={() => handleCheckboxChange('glenohumeralJointSclerosis')}
                            className="w-5 h-5 accent-pink-600"
                          />
                          <span>Sclerosis</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}